<template>
    <section v-if="visible" ref="suggestedGoalsSection" class="flex flex-col gap-3">
        <h2 ref="suggestedGoalsHeading" tabindex="-1" class="body-small-semibold text-ink">{{ t("assessments.results.suggestedGoals.title") }}</h2>
        <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ goalActionAnnouncement }}</p>

        <!-- Loading state -->
        <div v-if="loading" class="flex items-center gap-3 py-6 justify-center">
            <LoadingSpinner class="w-5 h-5 text-ink-muted" />
            <span class="body-small text-ink-muted">{{ t("assessments.results.suggestedGoals.generating") }}</span>
        </div>

        <!-- Error state -->
        <BaseNotification v-else-if="error" theme="error" :text="error" hide-dismiss />

        <!-- Goals list -->
        <div v-else-if="visibleGoals.length > 0" class="flex flex-col gap-3">
            <template v-for="goal in visibleGoals" :key="goal.id">
                <BaseCard v-if="developmentJourneysEnabled" as="article" padding="md" radius="lgr" class="flex min-w-0 flex-col gap-2">
                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                        <div class="flex min-w-0 items-start justify-between gap-4">
                            <div class="flex min-w-0 flex-1 items-start gap-2.5">
                                <!-- Hourglass "journey/goal" glyph — teal, distinct from the
                                     self-reflection amber so the goal cards read as a related
                                     but separate object type. Colors + path match the mock. -->
                                <svg class="goal-icon mt-1 shrink-0" viewBox="0 0 12.6317 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path
                                        d="M12.6316 15.1557C12.5958 13.1841 11.9474 11.5628 10.6863 10.2895C9.46316 9.05673 8.00842 8.43246 6.32211 8.42123C2.82737 8.39429 0.00210526 11.4438 0 15.1579H12.6316V15.1557Z"
                                        fill="#30A29D" />
                                    <path
                                        d="M0 1.26319C0.0357895 3.3587 0.684212 5.08189 1.94526 6.43514C3.16842 7.74543 4.62316 8.40893 6.30947 8.42086C9.80421 8.45189 12.6295 5.21077 12.6316 1.26319H0Z"
                                        fill="#81DADA" />
                                    <rect y="14.7368" width="12.6316" height="1.26316" fill="#30A29D" />
                                    <rect x="0.000115795" width="12.6316" height="1.26316" fill="#81DADA" />
                                </svg>
                                <h3 class="body-semibold min-w-0 flex-1 text-ink">{{ goal.title }}</h3>
                            </div>
                            <BaseButton
                                v-if="assessmentGoalDiscussionChatStartUrl"
                                theme="neutral"
                                size="sm"
                                class="shrink-0"
                                :aria-label="t('assessments.results.suggestedGoals.discussAriaLabel', { goalTitle: goal.title })"
                                :disabled="pendingId !== null || discussingGoalId !== null || (discussionRecoveryGoalId !== null && discussionRecoveryGoalId !== goal.id)"
                                @click="onDiscuss(goal)">
                                {{ discussingGoalId === goal.id ? t("assessments.results.suggestedGoals.startingDiscussion") : t("assessments.results.suggestedGoals.discuss") }}
                            </BaseButton>
                        </div>
                        <p v-if="goal.description" class="details-big text-ink-muted">{{ goal.description }}</p>
                    </div>

                    <div class="border-t border-border my-1"></div>

                    <div class="flex items-center justify-between gap-3">
                        <BaseButton
                            type="button"
                            theme="tertiary"
                            size="sm"
                            :aria-label="t('assessments.results.suggestedGoals.dismissAriaLabel', { goalTitle: goal.title })"
                            :disabled="pendingId !== null || discussingGoalId !== null || discussionRecoveryGoalId === goal.id"
                            @click="onDismiss(goal)">
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- a pending action shows only an ellipsis: punctuation, nothing to translate -->
                            {{ pendingId === goal.id ? "…" : t("common.dismiss") }}
                        </BaseButton>
                        <BaseButton
                            type="button"
                            theme="tertiary"
                            size="sm"
                            class="shrink-0"
                            data-suggested-goal-action
                            :aria-label="t('assessments.results.suggestedGoals.addGoalAriaLabel', { goalTitle: goal.title })"
                            :disabled="pendingId !== null || discussingGoalId !== null || discussionRecoveryGoalId === goal.id"
                            @click="onAddGoal(goal)">
                            {{ t("assessments.results.suggestedGoals.addGoal") }}
                        </BaseButton>
                    </div>
                </BaseCard>

                <BaseCard v-else padding="lg" class="flex flex-col gap-2.5">
                    <div class="flex flex-col gap-1">
                        <div class="flex items-start gap-2.5">
                            <svg class="goal-icon mt-1 shrink-0" viewBox="0 0 12.6317 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path
                                    d="M12.6316 15.1557C12.5958 13.1841 11.9474 11.5628 10.6863 10.2895C9.46316 9.05673 8.00842 8.43246 6.32211 8.42123C2.82737 8.39429 0.00210526 11.4438 0 15.1579H12.6316V15.1557Z"
                                    fill="#30A29D" />
                                <path
                                    d="M0 1.26319C0.0357895 3.3587 0.684212 5.08189 1.94526 6.43514C3.16842 7.74543 4.62316 8.40893 6.30947 8.42086C9.80421 8.45189 12.6295 5.21077 12.6316 1.26319H0Z"
                                    fill="#81DADA" />
                                <rect y="14.7368" width="12.6316" height="1.26316" fill="#30A29D" />
                                <rect x="0.000115795" width="12.6316" height="1.26316" fill="#81DADA" />
                            </svg>
                            <span class="body-semibold text-ink">{{ goal.title }}</span>
                        </div>
                        <p v-if="goal.description" class="body-small text-ink-muted pl-6">{{ goal.description }}</p>
                    </div>
                    <div class="flex items-center gap-3 flex-wrap pl-6">
                        <BaseButton theme="neutral" size="sm" :disabled="pendingId !== null || goal.prioritized_at !== null" @click="onAddGoal(goal)">
                            <template v-if="goal.prioritized_at"> <i class="bi bi-check-lg" aria-hidden="true"></i> {{ t("assessments.results.suggestedGoals.added") }} </template>
                            <template v-else> {{ t("assessments.results.suggestedGoals.addGoal") }} </template>
                        </BaseButton>
                        <BaseButton type="button" theme="tertiary" size="sm" :disabled="pendingId !== null" @click="onDismiss(goal)">
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- a pending action shows only an ellipsis: punctuation, nothing to translate -->
                            {{ pendingId === goal.id ? "…" : t("common.dismiss") }}
                        </BaseButton>
                    </div>
                </BaseCard>
            </template>

            <!-- Inline warning for prioritize failures (3-goal cap or request error) -->
            <BaseNotification v-if="goalActionError" theme="error" hide-dismiss>
                <template #content>
                    <p class="body-small">
                        <i class="bi bi-exclamation-triangle mr-2" aria-hidden="true"></i>{{ goalActionError }}
                        <BaseLink v-if="priorityCapExceeded" :href="goalsHref" class="base-link">{{ manageGoalsLabel }}</BaseLink>
                    </p>
                </template>
            </BaseNotification>

            <BaseNotification v-if="discussionError" theme="error" hide-dismiss>
                <template #content>
                    <p class="body-small">
                        {{ discussionError }}
                        <a v-if="discussionRecoveryUrl" :href="discussionRecoveryUrl" class="base-link">{{ t("assessments.results.suggestedGoals.openDiscussion") }}</a>
                    </p>
                </template>
            </BaseNotification>

            <div class="flex items-center justify-end pt-1">
                <BaseLink :href="goalsHref" class="body-small text-ink-muted hover:text-ink flex items-center gap-1" :before="onViewAllGoals">
                    {{ viewGoalsLabel }} <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </BaseLink>
            </div>
        </div>

        <div v-else-if="dismissedIds.size > 0" class="flex flex-col gap-3">
            <p class="body-small text-ink-muted py-2">{{ t("assessments.results.suggestedGoals.allAddressed") }}</p>
            <div class="flex items-center justify-end pt-1">
                <BaseLink :href="goalsHref" class="body-small text-ink-muted hover:text-ink flex items-center gap-1" :before="onViewAllGoals">
                    {{ viewGoalsLabel }} <i class="bi bi-arrow-right" aria-hidden="true"></i>
                </BaseLink>
            </div>
        </div>

        <p v-else class="body-small text-ink-muted py-2">{{ t("assessments.results.suggestedGoals.couldNotGenerate") }}</p>
    </section>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { fetchApi, useFetch } from "~vue/api";
import type { SuggestedGoalResponse, SuggestGoalsResponse } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

type SuggestedGoal = SuggestedGoalResponse;
type StartAssessmentGoalDiscussionResponse = { chat_url: string };

const props = defineProps<{
    cycleId: string;
    developmentJourneysEnabled?: boolean;
    assessmentGoalDiscussionChatStartUrl?: string | null;
    assessmentGoalAdoptionUrl?: string | null;
    assessmentGoalDismissalUrl?: string | null;
}>();

const emit = defineEmits<{
    // The parent reclaims the rail's space when there are no actionable cards.
    hidden: [];
}>();

const { t } = useI18n();

const MAX_PRIORITIZED_GOALS = 3;

const visible = ref(true);
const loading = ref(true);
const error = ref<string | null>(null);
const goals = ref<SuggestedGoal[]>([]);
const dismissedIds = ref<Set<string>>(new Set());
const pendingId = ref<string | null>(null);
const goalActionError = ref<string | null>(null);
const priorityCapExceeded = ref(false);
const discussingGoalId = ref<string | null>(null);
const discussionError = ref<string | null>(null);
const discussionRecoveryUrl = ref<string | null>(null);
const discussionRecoveryGoalId = ref<string | null>(null);
const discussionPayload = ref<{ goal_id: string } | null>(null);
const goalActionAnnouncement = ref("");
const suggestedGoalsSection = ref<HTMLElement | null>(null);
const suggestedGoalsHeading = ref<HTMLHeadingElement | null>(null);
const goalsHref = computed(() => (props.developmentJourneysEnabled ? "/development-journeys/" : "/journeys"));
const manageGoalsLabel = computed(() =>
    props.developmentJourneysEnabled ? t("assessments.results.suggestedGoals.manageGoalsInDevelopmentJourneys") : t("assessments.results.suggestedGoals.manageGoalsInJourneys"),
);
const viewGoalsLabel = computed(() =>
    props.developmentJourneysEnabled ? t("assessments.results.suggestedGoals.viewGoalsInDevelopmentJourneys") : t("assessments.results.suggestedGoals.viewAllGoalsInJourneys"),
);
const {
    data: discussionResponse,
    error: discussionRequestError,
    statusCode: discussionStatusCode,
    execute: executeStartDiscussion,
} = useFetch(() => props.assessmentGoalDiscussionChatStartUrl ?? "", { immediate: false })
    .post(discussionPayload)
    .json<StartAssessmentGoalDiscussionResponse>();

// Dismiss only hides locally after a successful server response — no need for a
// separate "dismissed" state since the archived goal disappears from
// /suggest-goals results on subsequent loads.
const visibleGoals = computed(() => goals.value.filter((g) => !dismissedIds.value.has(g.id)));

async function focusAfterGoalAction() {
    await nextTick();
    const nextGoalAction = suggestedGoalsSection.value?.querySelector<HTMLButtonElement>("[data-suggested-goal-action]");
    (nextGoalAction ?? suggestedGoalsHeading.value)?.focus();
}

async function fetchGoals() {
    loading.value = true;
    error.value = null;
    const { data, error: fetchError, statusCode } = await useFetch(`/v1/reflections/cycles/${props.cycleId}/suggest-goals`).post().json<SuggestGoalsResponse>();
    loading.value = false;
    if (fetchError.value || !data.value) {
        // If journeys flag is off, hide section entirely
        if (statusCode.value === 403) {
            visible.value = false;
            emit("hidden");
            return;
        }
        // No status at all means the request never completed — canceled by the
        // fetch layer (auth token fetch failed) or a network failure. Both hit the
        // try/catch before this was on useFetch; keep that path's copy.
        if (statusCode.value == null) {
            error.value = t("assessments.results.suggestedGoals.errors.fetchFailedTryAgain");
            return;
        }
        error.value = fetchError.value?.message || t("assessments.results.suggestedGoals.errors.fetchFailed");
        return;
    }
    const fetchedGoals = data.value.goals ?? [];
    // Confirmed suggestions are existing legacy goals whose progress is not transferred.
    goals.value = props.developmentJourneysEnabled ? fetchedGoals.filter((goal) => goal.source !== "confirmed") : fetchedGoals;
    if (props.developmentJourneysEnabled && fetchedGoals.length > 0 && goals.value.length === 0) {
        visible.value = false;
        emit("hidden");
    }
}

async function onTogglePrioritize(goal: SuggestedGoal) {
    if (pendingId.value !== null || discussingGoalId.value !== null) {
        return;
    }
    goalActionError.value = null;
    priorityCapExceeded.value = false;
    const isPrioritized = goal.prioritized_at !== null;

    pendingId.value = goal.id;
    try {
        // Prioritizing also confirms the goal (source: assessment -> confirmed) so it
        // appears as a regular prioritized goal on the Journeys board — same pattern
        // as adopting an extracted goal there.
        const response = await fetchApi(`/v1/goals/${goal.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(isPrioritized ? { is_prioritized: false } : { is_prioritized: true, confirm_goal: true }),
        });
        if (response.ok) {
            const updated = (await response.json()) as SuggestedGoal;
            const idx = goals.value.findIndex((g) => g.id === goal.id);
            if (idx !== -1) {
                goals.value[idx] = { ...goals.value[idx], prioritized_at: updated.prioritized_at };
            }
            if (!isPrioritized) {
                void logUserInteraction(AmplitudeEventNames.GOAL_PRIORITIZED, {
                    cycle_id: props.cycleId,
                    goal_id: goal.id,
                    page: "assessment_results",
                    method: "button",
                    new_state: "prioritized",
                });
            }
        } else if (response.status === 409) {
            priorityCapExceeded.value = true;
            goalActionError.value = t("assessments.results.suggestedGoals.errors.priorityCap", { maxGoals: formatNumber(MAX_PRIORITIZED_GOALS) });
        } else {
            goalActionError.value = t("assessments.results.suggestedGoals.errors.updateFailed");
        }
    } finally {
        pendingId.value = null;
    }
}

async function onAddGoal(goal: SuggestedGoal) {
    if (pendingId.value !== null || discussingGoalId.value !== null || discussionRecoveryGoalId.value === goal.id) {
        return;
    }
    if (!props.developmentJourneysEnabled) {
        await onTogglePrioritize(goal);
        return;
    }

    if (!props.assessmentGoalAdoptionUrl) {
        goalActionError.value = t("assessments.results.suggestedGoals.errors.addFailedTryAgain");
        return;
    }

    goalActionError.value = null;
    priorityCapExceeded.value = false;
    pendingId.value = goal.id;
    let added = false;
    try {
        const { error: adoptionError, response } = await useFetch(props.assessmentGoalAdoptionUrl).post({ goal_id: goal.id }).json<unknown>();
        if (!adoptionError.value && response.value?.ok) {
            dismissedIds.value.add(goal.id);
            goalActionAnnouncement.value = t("assessments.results.suggestedGoals.announcements.added", { goalTitle: goal.title });
            added = true;
        } else {
            goalActionError.value =
                response.value?.status === 400
                    ? adoptionError.value?.message || t("assessments.results.suggestedGoals.errors.addFailed")
                    : t("assessments.results.suggestedGoals.errors.addFailedTryAgain");
        }
    } catch {
        goalActionError.value = t("assessments.results.suggestedGoals.errors.addFailedTryAgain");
    } finally {
        pendingId.value = null;
    }
    if (added) {
        await focusAfterGoalAction();
    }
}

async function onDismiss(goal: SuggestedGoal) {
    if (pendingId.value !== null || discussingGoalId.value !== null || discussionRecoveryGoalId.value === goal.id) {
        return;
    }
    const dismissalUrl = props.developmentJourneysEnabled ? props.assessmentGoalDismissalUrl : `/v1/goals/${goal.id}`;
    if (!dismissalUrl) {
        return;
    }

    goalActionError.value = null;
    priorityCapExceeded.value = false;
    pendingId.value = goal.id;
    let dismissed = false;
    let alreadyUnavailable = false;
    try {
        if (props.developmentJourneysEnabled) {
            const { error: dismissalError, response } = await useFetch(dismissalUrl).post({ goal_id: goal.id });
            dismissed = !dismissalError.value && response.value?.ok === true;
            if (response.value?.status === 404) {
                await fetchGoals();
                alreadyUnavailable = !error.value && !goals.value.some((suggestion) => suggestion.id === goal.id);
            }
        } else {
            const response = await fetchApi(dismissalUrl, { method: "DELETE" });
            dismissed = response.ok || response.status === 404;
        }
        if (dismissed || alreadyUnavailable) {
            dismissedIds.value.add(goal.id);
            goalActionAnnouncement.value = dismissed
                ? t("assessments.results.suggestedGoals.announcements.dismissed", { goalTitle: goal.title })
                : t("assessments.results.suggestedGoals.announcements.noLongerAvailable", { goalTitle: goal.title });
        } else {
            goalActionError.value = t("assessments.results.suggestedGoals.errors.dismissFailedTryAgain");
        }
    } catch {
        goalActionError.value = t("assessments.results.suggestedGoals.errors.dismissFailedTryAgain");
    } finally {
        pendingId.value = null;
    }
    if (dismissed || alreadyUnavailable) {
        await focusAfterGoalAction();
    }
}

function openDiscussion(goal: SuggestedGoal, chatUrl: string) {
    discussingGoalId.value = goal.id;
    router.visit(chatUrl, {
        onCancel: () => {
            discussionError.value = t("assessments.results.suggestedGoals.errors.openDiscussionFailedTryAgain");
            discussionRecoveryUrl.value = chatUrl;
            discussionRecoveryGoalId.value = goal.id;
        },
        onError: () => {
            discussionError.value = t("assessments.results.suggestedGoals.errors.openDiscussionFailedTryAgain");
            discussionRecoveryUrl.value = chatUrl;
            discussionRecoveryGoalId.value = goal.id;
        },
        onFinish: () => {
            discussingGoalId.value = null;
        },
    });
}

async function onDiscuss(goal: SuggestedGoal) {
    if (
        !props.assessmentGoalDiscussionChatStartUrl ||
        pendingId.value !== null ||
        discussingGoalId.value !== null ||
        (discussionRecoveryGoalId.value !== null && discussionRecoveryGoalId.value !== goal.id)
    ) {
        return;
    }

    if (discussionRecoveryGoalId.value === goal.id && discussionRecoveryUrl.value) {
        discussionError.value = null;
        openDiscussion(goal, discussionRecoveryUrl.value);
        return;
    }

    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_SUGGESTED_GOAL_DISCUSS_CLICKED, {
        cycle_id: props.cycleId,
        goal_id: goal.id,
        page: "assessment_results",
    });
    discussionError.value = null;
    discussionRecoveryUrl.value = null;
    discussionRecoveryGoalId.value = null;
    discussingGoalId.value = goal.id;
    discussionPayload.value = { goal_id: goal.id };
    await executeStartDiscussion();

    const chatUrl = discussionResponse.value?.chat_url;
    if (discussionRequestError.value || !chatUrl) {
        discussionError.value =
            discussionStatusCode.value === 400
                ? discussionRequestError.value?.message || t("assessments.results.suggestedGoals.errors.discussionFailed")
                : t("assessments.results.suggestedGoals.errors.discussionFailedTryAgain");
        discussingGoalId.value = null;
        return;
    }

    openDiscussion(goal, chatUrl);
}

function onViewAllGoals() {
    return logUserInteraction(AmplitudeEventNames.ASSESSMENT_RESULTS_VIEW_ALL_GOALS_CLICKED, {
        cycle_id: props.cycleId,
    });
}

onMounted(() => {
    void fetchGoals();
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.goal-icon {
    width: 12.63px;
    height: 16px;
}
</style>
